import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import { Modal, Stack, Group, Card, Text, Button, Badge } from '@mantine/core';
import { useAtom } from 'jotai';
import { rechargeOverlayOpenAtom, diamondsAtom } from '@/stores/atoms/walletAtoms';
import { authUserAtom, authModalOpenAtom } from '@/stores/atoms/authAtoms';
import { notifications } from '@mantine/notifications';
import { resolveServerUrl, getApiBase } from '@/renderer/utils/session';
import { getStoredAuthToken } from '@/utils/userApi';
import { Capacitor } from '@capacitor/core';
import { fetchPaymentConfig } from '@/renderer/utils/paymentsApi';
// Client no longer reads env directly; config fetched from server
const plans = [
    { key: 'BASIC', name: '基础月费', price: '¥18/月', amount: 180 },
    { key: 'PRO', name: '专业月费', price: '¥38/月', amount: 400 },
    { key: 'ULTRA', name: '旗舰月费', price: '¥68/月', amount: 800 },
];
const RechargeOverlay = () => {
    const [opened, setOpened] = useAtom(rechargeOverlayOpenAtom);
    const [, setDiamonds] = useAtom(diamondsAtom);
    const [authUser] = useAtom(authUserAtom);
    const [, setAuthModalOpen] = useAtom(authModalOpenAtom);
    // Build backend Creem link with query params
    const createCreemBackendLink = (backendBase, productId) => {
        if (!backendBase || !productId)
            return '';
        const tk = getStoredAuthToken();
        const userId = authUser?.uid || '';
        if (!tk || !userId)
            return '';
        const base = backendBase.replace(/\/$/, '');
        const origin = globalThis.location?.origin;
        const success = origin ? `${origin}/creem?status=success&src=creem` : '';
        const cancel = origin ? `${origin}/creem?status=cancel&src=creem` : '';
        const u = new URL(`${base}/checkout`);
        u.searchParams.set('product_id', productId);
        u.searchParams.set('user_id', userId);
        if (success)
            u.searchParams.set('success_url', success);
        if (cancel)
            u.searchParams.set('cancel_url', cancel);
        // As external browsers cannot send Authorization headers, include token as query for backend compatibility
        u.searchParams.set('token', tk);
        return u.toString();
    };
    // If the URL points to our API host, append token param for auth (headers not available in external browser)
    const withAuthIfServerUrl = (href) => {
        try {
            if (!href)
                return href;
            const apiHost = new URL(getApiBase()).host;
            const url = new URL(href);
            if (url.host === apiHost) {
                const tk = getStoredAuthToken();
                const userId = authUser?.uid;
                if (userId)
                    url.searchParams.set('user_id', userId);
                if (tk)
                    url.searchParams.set('token', tk);
                return url.toString();
            }
            return href;
        }
        catch {
            // Not an absolute URL: leave as-is, it will be resolved elsewhere
            return href;
        }
    };
    const openLink = async (href) => {
        if (!href)
            return;
        try {
            if (Capacitor?.isNativePlatform?.()) {
                const { Browser } = await import('@capacitor/browser');
                await Browser.open({ url: href });
            }
            else if (window.electron?.ipcRenderer?.invoke) {
                window.electron.shell?.openExternal?.(href);
                // fallback
                if (!(window.electron.shell?.openExternal))
                    window.open(href, '_blank');
            }
            else {
                window.open(href, '_blank');
            }
        }
        catch {
            window.open(href, '_blank');
        }
    };
    const requireLogin = () => {
        const tk = getStoredAuthToken();
        if (!authUser || !tk) {
            try {
                setAuthModalOpen(true);
            }
            catch { }
            return false;
        }
        return true;
    };
    // Build Stripe/Store URLs from env for each plan
    const [cfg, setCfg] = React.useState(null);
    React.useEffect(() => {
        if (!opened)
            return;
        (async () => {
            try {
                const c = await fetchPaymentConfig();
                setCfg(c || {});
            }
            catch { }
        })();
    }, [opened]);
    // Enforce login requirement when opening recharge overlay
    React.useEffect(() => {
        if (!opened)
            return;
        const tk = getStoredAuthToken();
        if (!authUser || !tk) {
            try {
                setAuthModalOpen(true);
            }
            catch { }
        }
    }, [opened, authUser, setAuthModalOpen]);
    const buildUrls = (k) => {
        const stripe = withAuthIfServerUrl(resolveServerUrl(cfg?.stripe?.[k] || '') || '');
        const apple = withAuthIfServerUrl(resolveServerUrl(cfg?.apple?.[k] || '') || '');
        const google = withAuthIfServerUrl(resolveServerUrl(cfg?.google?.[k] || '') || '');
        const productId = cfg?.creem?.products?.[k]?.product_id || '';
        const backend = cfg?.creem?.backend_base || '';
        const creemBackend = backend && productId ? createCreemBackendLink(backend, productId) : '';
        return { stripe, apple, google, creemBackend };
    };
    // Native deep link 回调：监听 Creem 成功回跳（status=success&src=creem），完成后可以根据产品发钻
    React.useEffect(() => {
        if (!opened || !Capacitor?.isNativePlatform?.())
            return;
        let sub;
        (async () => {
            try {
                const { App } = await import('@capacitor/app');
                sub = await App.addListener('appUrlOpen', (data) => {
                    try {
                        const urlStr = data?.url || '';
                        // 兼容自定义 scheme（如 myapp://creem?status=success&src=creem）以及 myapp:/creem
                        let u = null;
                        try {
                            u = new URL(urlStr);
                        }
                        catch {
                            try {
                                u = new URL(urlStr.replace(/^(.*?):/, 'app://$1'));
                            }
                            catch { }
                        }
                        if (!u)
                            return;
                        const sp = u.searchParams;
                        if ((sp.get('src') || '').toLowerCase() === 'creem' && (sp.get('status') || '').toLowerCase() === 'success') {
                            notifications.show({ color: 'green', title: '充值成功', message: '会员已开通或续期', withCloseButton: true });
                            setOpened(false);
                        }
                    }
                    catch { }
                });
            }
            catch { }
        })();
        return () => { try {
            sub?.remove?.();
        }
        catch { } };
    }, [opened, setOpened]);
    return (_jsx(Modal, { opened: opened, onClose: () => setOpened(false), title: "\u5145\u503C\u4F1A\u5458", centered: true, zIndex: 5000, children: _jsxs(Stack, { gap: "sm", children: [plans.map((p) => {
                    const { stripe, apple, google, creemBackend } = buildUrls(p.key);
                    return (_jsx(Card, { withBorder: true, radius: "md", p: "md", children: _jsxs(Group, { justify: "space-between", align: "center", children: [_jsxs(Stack, { gap: 4, children: [_jsxs(Group, { gap: 8, align: "center", children: [_jsx(Text, { fw: 700, children: p.name }), _jsxs(Badge, { color: "yellow", variant: "light", children: ["\u6BCF\u6708\u9001", p.amount, "\u94BB\u77F3"] })] }), _jsx(Text, { c: "dimmed", size: "sm", children: p.price })] }), _jsxs(Group, { gap: 8, wrap: "nowrap", children: [_jsx(Button, { variant: "filled", onClick: () => { if (!requireLogin())
                                                return; openLink(stripe); }, disabled: !stripe || !authUser, children: "Stripe" }), _jsx(Button, { variant: "light", onClick: () => { if (!requireLogin())
                                                return; openLink(apple); }, disabled: !apple || !authUser, children: "Apple" }), _jsx(Button, { variant: "light", onClick: () => { if (!requireLogin())
                                                return; openLink(google); }, disabled: !google || !authUser, children: "Google" }), _jsx(Button, { variant: "light", onClick: () => { if (!requireLogin())
                                                return; openLink(creemBackend); }, disabled: !creemBackend || !authUser, children: "Creem" })] })] }) }, p.key));
                }), _jsx(Text, { size: "xs", c: "dimmed", children: "\u652F\u4ED8\u5B8C\u6210\u540E\u5C06\u81EA\u52A8\u5F00\u901A\u6216\u7EED\u671F\uFF0C\u94BB\u77F3\u4F1A\u5728\u5F00\u901A\u540E\u53D1\u653E\u3002" })] }) }));
};
export default RechargeOverlay;
